import React, { useState, useEffect, useRef } from "react";
import { Col, Pagination, Row } from "react-bootstrap";
import { ArrowUpDown, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { CiSearch } from "react-icons/ci";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { LuDownload } from "react-icons/lu";
import Chart from "../Charts/Chart";
import Dropdown from "react-bootstrap/Dropdown";
import { GripVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { useTheme } from "../../Themecontext/Themecontext";

const FASparentMultipalChild = ({
  columns = [],
  data = [],
  apiTotals = null,
  externalTotals = {},
  groupTotals = {},
}) => {
  console.log('data', externalTotals)
  const { Colourdata } = useSelector((state) => state.ClientLogin);
  const iscolour = Colourdata?.data?.find((el) => el.ExeName === "NEWFAS");
  const iscolourDark = Colourdata?.data?.find(
    (el) => el.ExeName === "NEWFASDARK",
  );
  const { theme } = useTheme();
  const isdark = theme;

  const containerRef = useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      const parent = containerRef.current.parentElement;
      if (parent) {
        parent.style.border = `2.5px solid ${isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour4}`;
      }
    }
  }, [isdark, iscolour, iscolourDark]);


  // Colour token helper – falls back gracefully
  const col = (
    light,
    dark,
    fallbackLight = "#ffffff",
    fallbackDark = "#1e293b",
  ) =>
    isdark === "light"
  const accentLight = iscolour?.Colour5 || "#d4af37";
  const baseWhite = "#ffffff";
  const darkBase = "#000000";

  const C = {
    bg: isdark === "light" ? `color-mix(in srgb, ${accentLight} 8%, ${baseWhite})` : iscolourDark?.Colour4,
    bgAlt: isdark === "light" ? `color-mix(in srgb, ${accentLight} 16%, ${baseWhite})` : iscolourDark?.Colour1,

    surface: isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour6,

    border: isdark === "light" ? iscolour?.Colour4 : iscolourDark?.Colour3,

    accent: isdark === "light" ? accentLight : iscolourDark?.Colour10,
    accentText: "#ffffff",

    text: isdark === "light" ? `color-mix(in srgb, ${accentLight} 60%, ${darkBase})` : iscolourDark?.Colour12,
    groupTextL0: isdark === "light" ? `color-mix(in srgb, ${accentLight} 30%, ${darkBase})` : iscolourDark?.Colour12,
    groupTextL1: isdark === "light" ? `color-mix(in srgb, ${accentLight} 40%, ${darkBase})` : iscolourDark?.Colour12,
    groupTextL2: isdark === "light" ? `color-mix(in srgb, ${accentLight} 50%, ${darkBase})` : iscolourDark?.Colour12,

    textMuted: isdark === "light" ? iscolour?.Colour11 : iscolourDark?.Colour12,

    btnBg: isdark === "light" ? iscolour?.Colour6 : iscolourDark?.Colour10,
    btnText: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour3,

    theadBg: isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour8,
    theadText: isdark === "light" ? "#000000" : iscolourDark?.Colour1,

    totalBg: isdark === "light" ? accentLight : iscolourDark?.Colour6,
    totalText: isdark === "light" ? "#ffffff" : iscolourDark?.Colour10,

    footerBg: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour7,
    footerText: isdark === "light" ? iscolour?.Colour1x : iscolourDark?.Colour4,

    pageBg: isdark === "light" ? iscolour?.Colour9 : iscolourDark?.Colour6,
    pageActive: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour9,
    pageText: isdark === "light" ? iscolour?.Colour11 : iscolourDark?.Colour3,

    inputBg: isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour4,
    inputText: isdark === "light" ? iscolour?.Colour11 : iscolourDark?.Colour12,
    inputBorder: isdark === "light" ? iscolour?.Colour4 : iscolourDark?.Colour3,
    placeholder:
      isdark === "light" ? iscolour?.Colour11 : iscolourDark?.Colour4,

    groupL0: isdark === "light" ? `color-mix(in srgb, ${accentLight} 50%, ${baseWhite})` : iscolourDark?.Colour6,
    groupL1: isdark === "light" ? `color-mix(in srgb, ${accentLight} 35%, ${baseWhite})` : iscolourDark?.Colour4,
    groupL2: isdark === "light" ? `color-mix(in srgb, ${accentLight} 20%, ${baseWhite})` : iscolourDark?.Colour2,
    subTotal: isdark === "light" ? `color-mix(in srgb, ${accentLight} 25%, ${baseWhite})` : iscolourDark?.Colour6,
  };

  /* ─── STATE ─── */
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [groupLevels, setGroupLevels] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isAllExpanded, setIsAllExpanded] = useState(true);

  const rowsPerPage = 10;

  const labelMap = columns.reduce((acc, c) => {
    acc[c.key] = c.label;
    return acc;
  }, {});

  /* ─── COLLECT ALL GROUP KEYS (for expand/collapse all) ─── */
  const getAllGroupKeys = (data, parentKey = "", level = 0) => {
    const keys = [];
    (data || []).forEach((item) => {
      const gk = `${parentKey}-${item.groupName}-${level}`;
      keys.push(gk);
      if (Array.isArray(item.children) && item.children[0]?.children) {
        keys.push(...getAllGroupKeys(item.children, gk, level + 1));
      }
    });
    return keys;
  };

  const handleExpandCollapseAll = () => {
    if (isAllExpanded) {
      // Collapse all – fill collapsedGroups with every group key
      const allKeys = getAllGroupKeys(hierarchyData);
      setCollapsedGroups(new Set(allKeys));
      setIsAllExpanded(false);
    } else {
      // Expand all – clear collapsedGroups
      setCollapsedGroups(new Set());
      setIsAllExpanded(true);
    }
  };

  /* ─── SEARCH ─── */
  const filteredData = (data ?? []).filter((row) =>
    Object.values(row).some((v) =>
      v?.toString().toLowerCase().includes(search.trim().toLowerCase()),
    ),
  );

  /* ─── SORT ─── */
  const handleSort = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "desc") {
        setSortConfig({ key, direction: "asc" });
        return;
      }
      if (sortConfig.direction === "asc") {
        setSortConfig({ key: null, direction: null });
        return;
      }
    }
    setSortConfig({ key, direction: "desc" });
  };

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let av = a[sortConfig.key],
      bv = b[sortConfig.key];
    const ca = String(av).replace(/,/g, ""),
      cb = String(bv).replace(/,/g, "");
    if (!isNaN(ca) && !isNaN(cb)) {
      av = Number(ca);
      bv = Number(cb);
    }
    if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
    if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const groupOrder = {
    Liabilities: 1,
    Assets: 2,
    Income: 3,
    Expense: 4,
  };
  const defaultOrderedData = [...sortedData].sort((a, b) => {
    return (groupOrder[a.AcLaieName] || 999) - (groupOrder[b.AcLaieName] || 999);
  });
  /* ─── PAGINATION ─── */
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = defaultOrderedData.slice(indexOfFirstRow, indexOfLastRow);

  const visiblePages = 4;
  let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
  let endPage = Math.min(totalPages, startPage + visiblePages - 1);
  if (endPage - startPage + 1 < visiblePages)
    startPage = Math.max(1, endPage - visiblePages + 1);

  /* ─── FORMAT ─── */
  const fmtIN = (n) => {
    const num = Number(n);
    return isNaN(num)
      ? n
      : num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  };
  const fmtRs = (n) => {
    if (n == null || n === "") return "0";
    const num = parseFloat(n);
    return isNaN(num) ? "0" : num.toLocaleString("en-IN");
  };
  const isNumeric = (colDef, rows) =>
    rows.some((r) => {
      const v = r[colDef.key];
      return v != null && v !== "" && !isNaN(String(v).replace(/,/g, ""));
    });

  const dashColumns = [];

  // Group By Hide Columns
  const hideGroupByColumns = [
    "NetBalDrAmt",
    "NetBalCrAmt",
    "srNumber",
    "BalDrAmt",
    "BalCrAmt",
    "OpCrAmt",
    "OpDrAmt",
    "TriCrAmt",
    "TriDrAmt",
    "BalAmt",
    "DrAmt",
    "CrAmt",
    "MarBal",
    "FebBal",
    "JanBal",
    "DecBal",
    "NovBal",
    "OctBal",
    "SepBal",
    "AugBal",
    "JulBal",
    "JunBal",
    "MayBal",
    "AprBal",
    "GroupSeq",
    "Quater4th",
    "Quater2nd",
    "Quater3rd",
    "Quater1st",
    "Amount",
    "Groupseq",
    "count"
  ];

  const shouldHideGroupByColumn = (columnKey) => {
    return hideGroupByColumns.includes(columnKey);
  };

  /* ─── CSV ─── */
  const downloadCSV = () => {
    if (!sortedData.length) return;
    const headers = columns.map((c) => c.label);
    const rows = sortedData.map((row) =>
      columns.map((c) => {
        let v = row[c.key];
        if (typeof v === "string" && v.includes(",")) v = `"${v}"`;
        return v ?? "";
      }),
    );
    const blob = new Blob(
      [[headers, ...rows].map((r) => r.join(",")).join("\n")],
      { type: "text/csv;charset=utf-8;" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", "table_data.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ─── HIERARCHY HELPERS ─── */
  const getLeafCount = (items) =>
    items.reduce(
      (n, item) => n + (item?.children ? getLeafCount(item.children) : 1),
      0,
    );

  const buildHierarchy = (rows, levels, levelIndex = 0) => {
    if (levelIndex >= levels.length) return rows || [];
    const key = levels[levelIndex];
    const grouped = {};

    rows.forEach((r) => {
      const v = r[key] || "Empty";
      (grouped[v] = grouped[v] || []).push(r);
    });

    const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
      if (key === "AcLaieName") {
        return (groupOrder[a] || 999) - (groupOrder[b] || 999);
      }

      return String(a).localeCompare(String(b));
    });

    return sortedGroups.map(([groupName, children]) => {
      const totals = {};
      columns.forEach((c) => {
        let t = 0;
        children.forEach((r) => {
          const v = String(r[c.key] || 0).replace(/,/g, "");
          if (!isNaN(v)) t += Number(v);
        });
        totals[c.key] = t;
      });
      const subChildren = buildHierarchy(children, levels, levelIndex + 1);
      return {
        groupName,
        level: levelIndex,
        totals,
        rowCount: getLeafCount(subChildren),
        children: subChildren,
      };
    });
  };

  const grandTotals = {};
  columns.forEach((c) => {
    let t = 0;
    sortedData.forEach((r) => {
      const v = String(r[c.key] || 0).replace(/,/g, "");
      if (!isNaN(v)) t += Number(v);
    });
    grandTotals[c.key] = t;
  });

  const hierarchyData =
    groupLevels.length > 0 ? buildHierarchy(sortedData, groupLevels) : [];

  const toggleGroup = (gk) =>
    setCollapsedGroups((prev) => {
      const s = new Set(prev);
      s.has(gk) ? s.delete(gk) : s.add(gk);
      return s;
    });

  const groupLevelBg = (level) =>
    [C.groupL0, C.groupL1, C.groupL2][Math.min(level, 2)];

  const getGroupTotal = (groupKey, columnKey, localTotal) => {
    if (
      groupTotals &&
      groupTotals[groupKey] &&
      groupTotals[groupKey][columnKey] !== undefined
    ) {
      return groupTotals[groupKey][columnKey];
    }

    return localTotal;
  };

  /* ─── SORTABLE ITEM (Group-by panel) ─── */
  const SortableItem = ({ id, label, checked, toggleItem }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });
    return (
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          touchAction: "none",
          cursor: "pointer",
          padding: "9px 12px",
          borderRadius: "8px",
          marginBottom: "8px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontWeight: "600",
          background: checked ? `${C.accent}18` : C.inputBg,
          border: `1.5px solid ${checked ? C.accent : C.border}`,
          color: C.text,
        }}
        onClick={toggleItem}
      >
        <div
          {...attributes}
          {...listeners}
          style={{ color: C.textMuted, cursor: "grab" }}
        >
          <GripVertical size={15} />
        </div>
        <div
          style={{
            width: 17,
            height: 17,
            borderRadius: "50%",
            flexShrink: 0,
            background: checked ? C.accent : "transparent",
            border: `1.5px solid ${checked ? C.accent : C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isdark === "light" ? "#fff" : iscolourDark?.Colour3 || "#000",
            fontSize: "10px",
          }}
        >
          {checked && "✓"}
        </div>
        <span style={{ fontSize: 13 }}>{label}</span>
      </div>
    );
  };

  /* ─── HIERARCHY RENDERER ─── */
  const RenderHierarchy = ({ data, columns, level = 0, parentKey = "" }) => {
     let prevRow = {};
    const getGroupTextColor = (lvl) => {
      if (lvl === 0) return C.groupTextL0;
      if (lvl === 1) return C.groupTextL1;
      return C.groupTextL2;
    };

    return (
      <>
        {data.map((item, idx) => {
          const gk = `${parentKey}-${item.groupName}-${level}`;
          const isCollapsed = collapsedGroups.has(gk);
          const isLastLevel =
            Array.isArray(item.children) &&
            item.children.length > 0 &&
            !item.children[0]?.children;
          const shortLabelMap = {
            NetBalCrAmt: "NetBal CR",
            NetBalDrAmt: "NetBal DR",
          };
          return (
            <React.Fragment key={idx}>
              {/* ── GROUP HEADER ROW ── */}
              <tr
                style={{
                  background: groupLevelBg(level),
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <td
                  colSpan={columns.length}
                  onClick={() => toggleGroup(gk)}
                  style={{
                    paddingLeft: `${level * 24 + 16}px`,
                    paddingTop: 8,
                    paddingBottom: 8,
                    fontWeight: 700,
                    color: getGroupTextColor(level),
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontSize: 15,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        background: C.accent,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: isdark === "light" ? "#fff" : iscolourDark?.Colour3 || "#000",
                        fontSize: 16,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {isCollapsed ? "+" : "−"}
                    </div>

                    <span>
                      {labelMap[groupLevels[level]]}: {item.groupName}
                    </span>
                  </div>
                </td>
              </tr>

              {/* TOTAL ROW */}
              {isCollapsed && (
                <tr
                  style={{
                    background: `${groupLevelBg(level)}CC`,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  {columns.map((c, ci) => {
                    // FIRST COLUMN
                    if (ci === 0) {
                      return (
                        <td
                          key={ci}
                          style={{
                            paddingLeft: `${level * 24 + 40}px`,
                            paddingTop: 8,
                            paddingBottom: 8,
                            fontWeight: 700,
                            color: C.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: 15,
                          }}
                        >
                          Total
                        </td>
                      );
                    }

                    const t = getGroupTotal(gk, c.key, item.totals[c.key]);

                    return (
                      <td
                        key={ci}
                        style={{
                          padding: "8px 12px",
                          fontWeight: 700,
                          color: C.text,
                          textAlign: c.align || "right",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: 15,
                        }}
                      >
                        {isNumeric(c, sortedData) && t != null && !isNaN(t)
                          ? c.rupees
                            ? fmtRs(t)
                            : c.format
                              ? fmtIN(t)
                              : t
                          : ""}
                      </td>
                    );
                  })}
                </tr>
              )}

              {!isCollapsed && (
                <>
                  {isLastLevel ? (
                    (item.children || []).map((row, ri) => {
                      const trStyle = {
                        background: ri % 2 === 0 ? C.bg : C.bgAlt,
                        transition: "background 0.12s",
                      };
                      return (
                        <tr
                          key={ri}
                          style={trStyle}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `${C.accent}18`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              ri % 2 === 0 ? C.bg : C.bgAlt;
                          }}
                        >
                          {columns.map((c, ci) => {
                            let val = row[c.key];
                            const gi = groupLevels.indexOf(c.key);
                            if (gi !== -1) {
                              let hide = true;
                              for (let i = 0; i <= gi; i++) {
                                if (
                                  prevRow[groupLevels[i]] !==
                                  row[groupLevels[i]]
                                ) {
                                  hide = false;
                                  break;
                                }
                              }
                              if (hide) val = "";
                            }
                            return (
                              <td
                                key={ci}
                                style={{
                                  textAlign: c.align || "left",
                                  padding: "15px 12px",
                                  fontSize: 14,
                                  color: C.text,
                                  verticalAlign: "middle",
                                  borderBottom: `1px solid ${C.border}`,
                                }}
                              >
                                {c.rupees
                                  ? fmtRs(val)
                                  : c.format
                                    ? fmtIN(val)
                                    : val}
                              </td>
                            );
                          })}
                          {(() => {
                            prevRow = row;
                            return null;
                          })()}
                        </tr>
                      );
                    })
                  ) : (
                    <RenderHierarchy
                      data={item.children}
                      columns={columns}
                      level={level + 1}
                      parentKey={gk}
                    />
                  )}

                  {/* SUB-TOTAL */}
                  {item.rowCount > 1 && (
                    <tr
                      style={{
                        background: C.subTotal,
                        borderTop: `2px solid ${C.accent}`,
                        fontSize: 15,
                      }}
                    >
                      {columns.map((c, ci) => {
                        if (ci === 0)
                          return (
                            <td
                              key={ci}
                              style={{
                                paddingLeft: `${(level + 1) * 32}px`,
                                fontWeight: 700,
                                color: C.accent,
                                padding: "9px 12px",
                              }}
                            >
                              {labelMap[groupLevels[level]]} Total
                            </td>
                          );
                        const t = getGroupTotal(gk, c.key, item.totals[c.key]);
                        if (dashColumns.includes(c.key))
                          return (
                            <td
                              key={ci}
                              style={{
                                fontWeight: 700,
                                textAlign: c.align || "left",
                                color: C.text,
                                padding: "9px 12px",
                              }}
                            >
                              —
                            </td>
                          );
                        return (
                          <td
                            key={ci}
                            style={{
                              fontWeight: 700,
                              textAlign: c.align || "left",
                              color: C.text,
                              padding: "9px 12px",
                            }}
                          >
                            {isNumeric(c, sortedData) && t != null && !isNaN(t)
                              ? c.rupees
                                ? fmtRs(t)
                                : c.format
                                  ? fmtIN(t)
                                  : t
                              : ""}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  /* ─── DRAG END ─── */
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oi = groupLevels.indexOf(active.id);
    const ni = groupLevels.indexOf(over.id);
    setGroupLevels((items) => arrayMove(items, oi, ni));
  };

  return (
    <>
      {/* ─── global scoped styles ─── */}
      <style>{`
                .fas-pmc-wrap *::-webkit-scrollbar { width: 6px; height: 6px; }
                .fas-pmc-wrap *::-webkit-scrollbar-track { background: ${C.surface}; }
                .fas-pmc-wrap *::-webkit-scrollbar-thumb { background: ${C.accent}; border-radius: 10px; }
                /* ── Remove ALL vertical column divider lines ── */
                .fas-pmc-wrap table td,
                .fas-pmc-wrap table th {
                    border-right: none !important;
                    border-left: none !important;
                }
                .fas-pmc-pg .page-item .page-link {
                    border: none; border-radius: 7px; min-width: 33px; height: 33px;
                    display: flex; align-items: center; justify-content: center;
                    background: ${C.pageBg}; color: ${C.pageText}; font-weight: 600; font-size: 13px; margin: 0 2px;
                    transition: background 0.15s, color 0.15s;
                }
                .fas-pmc-pg .page-item.active .page-link {
                    background: ${C.pageActive} !important; color: #fff !important;
                    box-shadow: 0 2px 10px ${C.pageActive}55;
                }
                .fas-pmc-pg .page-item.disabled .page-link { opacity: 0.4; }
                .fas-pmc-pg .page-item .page-link:hover:not([aria-current]) { background: ${C.accent}33; color: ${C.accent}; }
                .fas-pmc-search:focus { outline: none; border-color: ${C.accent} !important; box-shadow: 0 0 0 3px ${C.accent}22 !important; }
                .fas-pmc-dl:hover { opacity: 0.85; transform: scale(1.02); }

                 .custom-pagination {
      gap: 8px;
    }

    .custom-pagination .page-item .page-link {
      border: none;
      border-radius: 8px;
      min-width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour6};
      color: ${isdark === "light" ? "#000" : iscolourDark?.Colour3};
      font-weight: 500;
    }

    .custom-pagination .page-item.active .page-link {
      background: ${isdark === "light" ? iscolour?.Colour10 : iscolourDark?.Colour9};
        color: ${isdark === "light" ? "#ffffff" : iscolourDark?.Colour3};
    }

    .custom-pagination .page-item .page-link:hover {
      background: ${isdark === "light" ? iscolour?.Colour10 : iscolourDark?.Colour11};
       color: ${isdark === "light" ? "#ffffff" : iscolourDark?.Colour3};
    }

    .search-input::placeholder {
      color: ${isdark === "light" ? iscolour?.Colour10 : iscolourDark?.Colour7};
      opacity: 1;
    }

    @media (max-width: 991px) {
      .search-input {
        font-size: 13px !important;
      }
    }

    @media (max-width: 768px) {
      .top-bar-wrap {
        padding-left: 6px !important;
        padding-right: 6px !important;
      }

      .search-wrapper {
        flex: 1 !important;
        width: auto !important;
        min-width: 0 !important;
      }
       .hover-scale-btn {
          transition: transform 0.2s ease-in-out;
        }
        .hover-scale-btn:hover {
          transform: scale(1.02);
        }
      .download-btn {
        padding: 8px 10px !important;
        min-width: 40px;
        width: 40px;
        height: 40px;
      }

      .download-text {
        display: none;
      }
    }

    @media (max-width: 576px) {
      .top-bar-wrap {
        gap: 8px !important;
      }
    }
            `}</style>

      <div
        ref={containerRef}
        className="fas-pmc-wrap"
        style={{
          background: C.surface,
          borderRadius: "0 0 12px 12px",
        }}
      >
        {/* ══ TOP BAR ══ */}

        <Row
          className="g-2 align-items-center m-0 px-2 px-sm-2 px-md-3"
          style={{
            borderBottom: `1px solid ${C.border}`,
            padding: "12px 0",
          }}
        >
          {/* SEARCH */}
          <Col xs={12} md={12} lg={12} xl={4}>
            <div style={{ position: "relative", width: "100%" }}>
              <CiSearch
                size={18}
                style={{
                  position: "absolute",
                  left: 11,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: C.textMuted,
                  pointerEvents: "none",
                }}
              />

              <input
                type="text"
                placeholder="Search..."
                className="search-input fas-pmc-search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  paddingLeft: 36,
                  height: 40,
                  width: "100%",
                  fontSize: 14,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 10,
                  background: C.inputBg,
                  color: C.inputText,
                }}
              />
            </div>
          </Col>

          {/* BUTTON SECTION */}
          <Col xs={12} md={12} lg={12} xl={8}>
            <Row className="g-2 justify-content-center justify-content-md-center justify-content-xl-end">
              {/* GROUP BY */}
              <Col xs={12} sm="auto">
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <Dropdown autoClose="outside" drop="down">
                    <Dropdown.Toggle
                      id="fas-group-toggle"
                      className="w-100"
                      style={{
                        minHeight: 42,
                        width: "100%",
                        padding: "0 16px",
                        fontSize: 13,
                        fontWeight: 700,
                        background: C.inputBg,
                        color: C.accent,
                        border: `1.5px solid ${C.accent}`,
                        borderRadius: 9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        boxShadow: "none",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ fontSize: 15 }}>☰</span>

                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Group By ({groupLevels.length})
                      </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      align="end"
                      renderOnMount
                      popperConfig={{
                        strategy: "fixed",
                      }}
                      style={{
                        width: "100%",
                        minWidth: 280,
                        maxWidth: 340,
                        padding: 0,
                        borderRadius: 14,
                        border: `1px solid ${C.border}`,
                        overflow: "hidden",
                        boxShadow: "0 10px 35px rgba(0,0,0,0.18)",
                        background: C.inputBg,
                        zIndex: 9999,
                        position: "absolute",
                      }}
                    >
                      {/* Header */}
                      <div
                        style={{
                          padding: "12px 14px",
                          background: C.accent,
                        }}
                      >
                        <Row className="align-items-center gy-2">
                          <Col xs={12} sm={6}>
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 14,
                                color: isdark === "light" ? "#fff" : iscolourDark?.Colour3 || "#000",
                              }}
                            >
                              Reorder & Group By
                            </span>
                          </Col>

                          <Col xs={12} sm={6}>
                            <div
                              className="d-flex justify-content-sm-end gap-3 flex-wrap"
                              style={{
                                fontSize: 12,
                                color: isdark === "light" ? "#fff" : iscolourDark?.Colour3 || "#000",
                                fontWeight: 600,
                              }}
                            >
                              <span
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={() =>
                                  setGroupLevels(
                                    columns
                                      .filter(
                                        (c) => !shouldHideGroupByColumn(c.key),
                                      )
                                      .map((c) => c.key),
                                  )
                                }
                              >
                                Select All
                              </span>

                              <span
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={() => setGroupLevels([])}
                              >
                                Clear
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {/* Body */}
                      <div
                        style={{
                          padding: 10,
                          maxHeight: 360,
                          overflowY: "auto",
                        }}
                      >
                        <DndContext
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                          modifiers={[
                            restrictToVerticalAxis,
                            restrictToParentElement,
                          ]}
                        >
                          <SortableContext
                            items={groupLevels}
                            strategy={verticalListSortingStrategy}
                          >
                            {groupLevels.map((item) => {
                              const column = columns.find(
                                (c) => c.key === item,
                              );

                              if (!column) return null;

                              return (
                                <SortableItem
                                  key={item}
                                  id={item}
                                  label={column.label}
                                  checked={true}
                                  toggleItem={() =>
                                    setGroupLevels(
                                      groupLevels.filter((g) => g !== item),
                                    )
                                  }
                                />
                              );
                            })}

                            {columns
                              .filter(
                                (c) =>
                                  !groupLevels.includes(c.key) &&
                                  !shouldHideGroupByColumn(c.key),
                              )
                              .map((col) => (
                                <div
                                  key={col.key}
                                  onClick={() =>
                                    setGroupLevels([...groupLevels, col.key])
                                  }
                                  style={{
                                    padding: "10px 12px",
                                    borderRadius: 10,
                                    marginBottom: 8,
                                    background: C.inputBg,
                                    border: `1px solid ${C.border}`,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                    color: C.text,
                                    fontSize: 13,
                                    width: "100%",
                                    overflow: "hidden",
                                  }}
                                >
                                  <GripVertical
                                    size={15}
                                    style={{
                                      color: C.textMuted,
                                      flexShrink: 0,
                                    }}
                                  />

                                  <div
                                    style={{
                                      minWidth: 17,
                                      width: 17,
                                      height: 17,
                                      borderRadius: "50%",
                                      border: `1.5px solid ${C.border}`,
                                      flexShrink: 0,
                                    }}
                                  />

                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {col.label}
                                  </span>
                                </div>
                              ))}
                          </SortableContext>
                        </DndContext>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Col>

              {/* EXPAND COLLAPSE */}
              <Col xs={12} sm="auto">
                <button
                  onClick={
                    groupLevels.length > 0 ? handleExpandCollapseAll : undefined
                  }
                  disabled={groupLevels.length === 0}
                  style={{
                    height: 40,
                    padding: "0 16px",
                    // minWidth: 140,
                    background: C.btnBg,
                    color: C.btnText,
                    border: "none",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    cursor:
                      groupLevels.length === 0 ? "not-allowed" : "pointer",
                    opacity: groupLevels.length === 0 ? 0.55 : 1,
                  }}
                >
                  <span style={{ fontSize: 16 }}>
                    {isAllExpanded ? "⊟" : "⊞"}
                  </span>

                  {isAllExpanded ? "Collapse All" : "Expand All"}
                </button>
              </Col>

              {/* DOWNLOAD */}
              <Col xs={12} sm="auto">
                <button
                  onClick={downloadCSV}
                  className="fas-pmc-dl"
                  style={{
                    height: 40,
                    padding: "0 18px",
                    // minWidth: 140,
                    background: C.btnBg,
                    color: C.btnText,
                    border: "none",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    cursor: "pointer",
                  }}
                >
                  <LuDownload size={16} />
                  <span>Download CSV</span>
                </button>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* ══ TABLE ══ */}
        <div
          className="grey-scroll"
          style={{
            height: "400px",
            position: "relative",
            zIndex: 1,
            overflowX: "auto",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor:
              isdark === "light"
                ? `${iscolour?.Colour7} ${iscolour?.Colour3}`
                : `${iscolourDark?.Colour7} ${iscolourDark?.Colour3}`,
          }}
        >
          {sortedData.length > 0 ? (
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: 700,
                tableLayout: "auto",
              }}
            >
              {/* THEAD */}
              <thead>
                <tr>
                  {columns.map((c, i) => (
                    <th
                      key={i}
                      onClick={() => handleSort(c.key)}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        background: C.theadBg,
                        color: C.theadText,
                        padding: "13px 12px",
                        fontWeight: 700,
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        borderBottom: `3px solid ${C.accent}cc`,
                        borderRight: "none",
                        textAlign: "center",
                        userSelect: "none",
                        transition: "background 0.15s",
                      }}
                    // onMouseEnter={(e) => { e.currentTarget.style.background = `${C.accent}dd`; }}
                    // onMouseLeave={(e) => { e.currentTarget.style.background = C.theadBg; }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 5,
                        }}
                      >
                        {c.label}
                        {sortConfig.key === c.key ? (
                          sortConfig.direction === "asc" ? (
                            <ArrowUp size={13} />
                          ) : (
                            <ArrowDown size={13} />
                          )
                        ) : (
                          <ArrowUpDown size={13} style={{ opacity: 0.5 }} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* TBODY */}
              <tbody>
                {groupLevels.length > 0 ? (
                  <RenderHierarchy
                    data={hierarchyData}
                    columns={columns}
                    level={0}
                  />
                ) : (
                  currentRows.map((row, ri) => (
                    <tr
                      key={ri}
                      style={{
                        background: ri % 2 === 0 ? C.bg : C.bgAlt,
                        transition: "background 0.12s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = `${C.accent}18`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          ri % 2 === 0 ? C.bg : C.bgAlt;
                      }}
                    >
                      {columns.map((c, ci) => {
                        const val =
                          c.key === "srNumber"
                            ? (row.srNumber ?? indexOfFirstRow + ri + 1)
                            : row[c.key];
                        return (
                          <td
                            key={ci}
                            style={{
                              padding: "15px 12px",
                              fontSize: "14px",
                              fontWeight: "600",
                              textAlign: c.align || "left",
                              color: C.text,
                              verticalAlign: "middle",
                              borderBottom: `1px solid ${C.border}`,
                              borderRight: "none",
                              whiteSpace: "nowrap", // 👈 line break nahi
                              // width: "1%", // 👈 content jitni width
                            }}
                          >
                            {c.key === "charts" ? (
                              <div
                                onClick={() => setSelectedRow(row)}
                                className="hover-scale-btn"
                                style={{
                                  background: isdark === "light" ? iscolour?.Colour10 : iscolourDark?.Colour9,
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "8px",
                                  color: isdark === "light" ? "#ffffff" : iscolourDark?.Colour3,
                                  margin: "auto",
                                  cursor: "pointer",
                                }}
                              >
                                <BarChart3 size={16} />
                              </div>
                            ) : c.rupees ? (
                              fmtRs(val)
                            ) : c.format ? (
                              fmtIN(val)
                            ) : (
                              val
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}

                {/* ── GRAND TOTAL ── */}
                <tr
                  style={{
                    background: C.totalBg,
                    fontWeight: 800,
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1,
                    borderTop: `3px solid ${C.accent}`,
                    fontSize: 14,
                  }}
                >
                  {columns.map((c, ci) => {
                    if (ci === 0)
                      return (
                        <td
                          key={ci}
                          style={{
                            padding: "12px 14px",
                            fontWeight: 800,
                            color: C.totalText,
                          }}
                        >
                          Grand Total
                        </td>
                      );
                    const extTot = Array.isArray(externalTotals) ? externalTotals[0] : externalTotals;
                    const apiTot = Array.isArray(apiTotals) ? apiTotals[0] : apiTotals;
                    const isExtTot = extTot && (extTot[c.key] !== undefined || extTot[c.label] !== undefined);
                    const isApiTot = apiTot && (apiTot[c.key] !== undefined || apiTot[c.label] !== undefined);
                    const t = isExtTot 
                      ? (extTot[c.key] !== undefined ? extTot[c.key] : extTot[c.label]) 
                      : isApiTot 
                        ? (apiTot[c.key] !== undefined ? apiTot[c.key] : apiTot[c.label]) 
                        : grandTotals[c.key];
                    if (dashColumns.includes(c.key))
                      return (
                        <td
                          key={ci}
                          style={{
                            fontWeight: 800,
                            textAlign: c.align || "left",
                            color: C.totalText,
                            padding: "12px 14px",
                          }}
                        >
                          —
                        </td>
                      );
                    return (
                      <td
                        key={ci}
                        style={{
                          fontWeight: 800,
                          textAlign: c.align || "left",
                          color: C.totalText,
                          padding: "12px 14px",
                        }}
                      >
                        {(isExtTot || isApiTot || isNumeric(c, sortedData)) && t != null && t !== "" && !isNaN(t)
                          ? c.rupees
                            ? fmtRs(t)
                            : c.format
                              ? fmtIN(t)
                              : t
                          : ""}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          ) : (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  isdark === "light"
                    ? iscolour?.Colour3
                    : iscolourDark?.Colour5,
                overflow: "hidden",
                padding: "20px",
              }}
            >
              {/* BACKGROUND CIRCLE 1 */}
              <div
                style={{
                  position: "absolute",
                  width: "clamp(150px, 25vw, 250px)",
                  height: "clamp(150px, 25vw, 250px)",
                  borderRadius: "50%",
                  background:
                    isdark === "light"
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.03)",
                  top: "-60px",
                  right: "-60px",
                }}
              />

              {/* BACKGROUND CIRCLE 2 */}
              <div
                style={{
                  position: "absolute",
                  width: "clamp(120px, 20vw, 180px)",
                  height: "clamp(120px, 20vw, 180px)",
                  borderRadius: "50%",
                  background:
                    isdark === "light"
                      ? "rgba(0,0,0,0.03)"
                      : "rgba(255,255,255,0.03)",
                  bottom: "-40px",
                  left: "-40px",
                }}
              />

              {/* PREMIUM EMPTY STATE CARD */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: "450px",
                  padding: "40px",
                  borderRadius: "24px",
                  background: isdark === "light"
                    ? "rgba(255, 255, 255, 0.6)"
                    : "rgba(30, 30, 35, 0.6)",
                  boxShadow: isdark === "light"
                    ? "0 8px 32px rgba(0, 0, 0, 0.05)"
                    : "0 8px 32px rgba(0, 0, 0, 0.3)",
                  backdropFilter: "blur(12px)",
                  border: isdark === "light"
                    ? "1px solid rgba(255,255,255,0.8)"
                    : "1px solid rgba(255,255,255,0.05)",
                  textAlign: "center",
                  margin: "20px auto",
                }}
              >
                {/* ICON CONTAINER WITH GLOW */}
                <div
                  style={{
                    width: "90px",
                    height: "90px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "24px",
                    background: isdark === "light"
                      ? `${iscolour?.Colour11 || "#1e293b"}1A`
                      : `${iscolourDark?.Colour12 || "#f1f5f9"}26`,
                    boxShadow: isdark === "light"
                      ? `0 10px 25px -5px ${iscolour?.Colour11 || "#1e293b"}33`
                      : `0 10px 25px -5px ${iscolourDark?.Colour12 || "#f1f5f9"}4D`,
                    color: isdark === "light" ? "#1e293b" : "#f1f5f9",
                  }}
                >
                  {search ? (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                      <path d="m9 9 4 4" />
                      <path d="m13 9-4 4" />
                    </svg>
                  ) : (
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
                      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                    </svg>
                  )}
                </div>

                <h2
                  style={{
                    margin: "0 0 12px 0",
                    fontWeight: "700",
                    fontSize: "22px",
                    color: isdark === "light" ? "#1e293b" : "#f1f5f9",
                    letterSpacing: "-0.5px"
                  }}
                >
                  {search ? (
                    <span>
                      No results for <span style={{ color: isdark === "light" ? "#1e293b" : "#f1f5f9" }}>"{search}"</span>
                    </span>
                  ) : (
                    "No Data Available"
                  )}
                </h2>

                <p
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    lineHeight: "1.6",
                    color: isdark === "light" ? "#64748b" : "#94a3b8",
                  }}
                >
                  {search
                    ? "We couldn't find anything matching your search. Please try a different term or clear the filter."
                    : "There are currently no records to display."
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ══ FOOTER BAR ══ */}
        {sortedData.length > 0 && (
          <div style={{ margin: "0px 10px" }}>
            <Row
              style={{
                backgroundColor:
                  isdark === "light"
                    ? iscolour?.Colour5
                    : iscolourDark?.Colour7,
                padding: "10px",
              }}
            >
              <Col
                sm={12}
                md={6}
                lg={6}
                className="text-center text-md-start p-0 pb-md-2"
                style={{
                  fontSize: "13px",
                  color: isdark === "light" ? "#000" : (iscolourDark?.Colour1 || "#fff")
                }}
              >
                <div>
                  Showing {indexOfFirstRow + 1} to{" "}
                  {Math.min(indexOfLastRow, sortedData.length)} of{" "}
                  {sortedData.length} results
                </div>
              </Col>

              <Col
                sm={12}
                md={6}
                lg={6}
                className="d-flex justify-content-center p-0 justify-content-md-end"
              >
                <Pagination className="custom-pagination">
                  {/* FIRST PAGE */}
                  <Pagination.First
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  />

                  {/* PREVIOUS */}
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  />

                  {/* PAGE NUMBERS */}
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                    const page = startPage + i;

                    return (
                      <Pagination.Item
                        key={page}
                        active={currentPage === page}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}

                  {/* NEXT */}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  />

                  {/* LAST PAGE */}
                  <Pagination.Last
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  />
                </Pagination>
              </Col>
            </Row>
          </div>
        )}
      </div>

      {/* ══ CHART MODAL ══ */}
      {selectedRow && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setSelectedRow(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "90%",
              maxWidth: 1100,
              background: C.inputBg,
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 12,
              }}
            >
              <button
                onClick={() => setSelectedRow(null)}
                style={{
                  background: C.accent,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 16px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
            <Chart data={selectedRow} labelMap={labelMap} />
          </div>
        </div>
      )}
    </>
  );
};

export default FASparentMultipalChild;
